import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Calendar, Car, MapPin, X } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { SearchLocationDropdown } from './search/SearchLocationDropdown';
import { SearchDatePicker } from './search/SearchDatePicker';
import { SearchTypeSelector } from './search/SearchTypeSelector';

interface HeaderSearchBarProps {
  className?: string;
}

type ActiveSection = 'where' | 'when' | 'what' | null;

export const HeaderSearchBar: React.FC<HeaderSearchBarProps> = ({ className }) => {
  const navigate = useNavigate();
  const [location, setLocation] = useState('');
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [listingType, setListingType] = useState<string>('all');
  const [activeSection, setActiveSection] = useState<ActiveSection>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current && 
        !containerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setActiveSection(null);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (location) params.set('location', location);
    if (date) params.set('date', format(date, 'yyyy-MM-dd'));
    
    // Parse type and category
    if (listingType && listingType !== 'all') {
      if (listingType.includes(':')) {
        const [type, category] = listingType.split(':');
        params.set('type', type);
        params.set('category', category);
      } else {
        params.set('type', listingType);
      }
    }
    
    setActiveSection(null);
    navigate(`/search?${params.toString()}`);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
    if (e.key === 'Escape') {
      setActiveSection(null);
    }
  };

  const getTypeDisplayValue = () => {
    if (listingType === 'all') return 'All types';
    if (listingType.includes(':')) {
      const [type, category] = listingType.split(':');
      return category;
    }
    return listingType === 'events' ? 'Events' : 'Experiences';
  };

  return (
    <div className={cn("w-full max-w-3xl mx-auto relative", className)} ref={containerRef}>
      {/* Search Bar */}
      <div className={cn(
        "flex items-center bg-background border border-border rounded-full shadow-md hover:shadow-lg transition-shadow",
        activeSection && "shadow-lg"
      )}>
        {/* Location Input */}
        <button
          onClick={() => setActiveSection(activeSection === 'where' ? null : 'where')}
          className={cn(
            "flex-1 px-6 py-3 text-left transition-colors rounded-l-full",
            activeSection === 'where' ? "bg-muted" : "hover:bg-secondary/50"
          )}
        >
          <label className="block text-xs font-medium text-foreground mb-0.5">Where</label>
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <span className={cn("text-sm truncate", location ? "text-foreground" : "text-muted-foreground")}>
              {location || "Search locations..."}
            </span>
          </div>
        </button>

        <div className="w-px h-8 bg-border" />

        {/* Date Picker */}
        <button
          onClick={() => setActiveSection(activeSection === 'when' ? null : 'when')}
          className={cn(
            "flex-1 px-6 py-3 text-left transition-colors",
            activeSection === 'when' ? "bg-muted" : "hover:bg-secondary/50"
          )}
        >
          <label className="block text-xs font-medium text-foreground mb-0.5">When</label>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <span className={cn("text-sm", date ? "text-foreground" : "text-muted-foreground")}>
              {date ? format(date, "MMM d, yyyy") : "Any date"}
            </span>
          </div>
        </button>

        <div className="w-px h-8 bg-border" />

        {/* Type Selector */}
        <button
          onClick={() => setActiveSection(activeSection === 'what' ? null : 'what')}
          className={cn(
            "flex-1 px-6 py-3 text-left transition-colors",
            activeSection === 'what' ? "bg-muted" : "hover:bg-secondary/50"
          )}
        >
          <label className="block text-xs font-medium text-foreground mb-0.5">What</label>
          <div className="flex items-center gap-2">
            <Car className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <span className={cn("text-sm", listingType !== 'all' ? "text-foreground" : "text-muted-foreground")}>
              {getTypeDisplayValue()}
            </span>
          </div>
        </button>

        {/* Search Button */}
        <button
          onClick={handleSearch}
          className="m-2 p-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full transition-colors flex-shrink-0"
        >
          <Search className="w-5 h-5" />
        </button>
      </div>

      {/* Dropdown Container - Full Width */}
      {activeSection && (
        <div 
          ref={dropdownRef}
          className="absolute top-[calc(100%+8px)] left-0 right-0 bg-background border border-border rounded-2xl shadow-xl z-[2100] overflow-hidden"
        >
          {activeSection === 'where' && (
            <div className="relative">
              {/* Input field in dropdown */}
              <div className="p-4 border-b border-border">
                <div className="relative">
                  <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search locations..."
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    onKeyDown={handleKeyDown}
                    autoFocus
                    className="w-full pl-12 pr-10 py-3 text-base border border-border rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                  {location && (
                    <button
                      onClick={() => setLocation('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-muted rounded-full"
                    >
                      <X className="w-4 h-4 text-muted-foreground" />
                    </button>
                  )}
                </div>
              </div>
              <SearchLocationDropdown
                value={location}
                onChange={setLocation}
                onClose={() => setActiveSection(null)}
              />
            </div>
          )}

          {activeSection === 'when' && (
            <SearchDatePicker
              value={date}
              onChange={setDate}
              onClose={() => setActiveSection(null)}
            />
          )}

          {activeSection === 'what' && (
            <SearchTypeSelector
              value={listingType}
              onChange={setListingType}
              onClose={() => setActiveSection(null)}
            />
          )}
        </div>
      )}
    </div>
  );
};
